import type { Pence } from './money.js';
import type { PricedLine } from './pricing.js';

/*
 * What the customer app knows about a code: the label, the note and a simple
 * "offer" it can preview before the server quotes (CLAUDE.md §4). The engine
 * that decides whether a code applies, and what it gives, is engine.ts.
 */

export type Offer =
  | { type: 'amount_off'; pence: Pence; appliesTo: 'main' }
  | { type: 'free_item'; itemId: string };

export function normaliseCode(input: string): string {
  return input.trim().toUpperCase().replace(/\s+/g, '');
}

/**
 * The app's instant preview of a simple code before the server answers (the server's
 * quote wins). `amount_off` applies to the most expensive main that isn't already free,
 * never more than that main's price. `free_item` codes add a £0 line instead.
 */
export function discountForOffer(offer: Offer, lines: readonly PricedLine[]): Pence {
  if (offer.type !== 'amount_off') return 0;
  const mains = lines.filter((l) => l.isMain && !l.isReward);
  if (mains.length === 0) return 0;
  const dearest = mains.reduce((a, b) => (b.unitPence > a.unitPence ? b : a));
  return Math.min(offer.pence, dearest.unitPence);
}

/** The item behind an old 'referral_item' voucher whose offer has since changed; also the fallback when the referral offer isn't a free item. */
export const REFERRAL_REWARD_ITEM_ID = 'totsS';

/**
 * Refer a friend: what each side gets, read from the built-in referral offer (Admin → Offers and codes → Refer a friend).
 * Points (CLAUDE.md §4, Saif 24 Oct 2026: 100 each) land in both ledgers when the friend's first order is collected;
 * a free item is a £0 line on the friend's first order and a voucher for the referrer at that collection.
 */
export type ReferralReward =
  | { kind: 'points'; points: number; itemId: null; name: null }
  | { kind: 'free_item'; points: 0; itemId: string; name: string };

/** "100 points" or "free Tikki Tots", for "You both get …". */
export function referralRewardText(r: ReferralReward | null | undefined): string {
  if (!r) return '100 points';
  return r.kind === 'points' ? `${r.points.toLocaleString('en-GB')} points` : `free ${r.name}`;
}

